import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Shield, Smartphone, Mail, CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/useToast';
export function TwoFactorAuth() {
  const {
    toast
  } = useToast();
  const [isEnabled, setIsEnabled] = useState(false);
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [method, setMethod] = useState<'sms' | 'email'>('sms');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState(1); // 1: Select, 2: Verify, 3: Success
  const handleStartSetup = () => {
    setIsSettingUp(true);
    setStep(1);
  };
  const handleSendCode = () => {
    // Simulate sending code
    toast.info(`Verification code sent to your ${method === 'sms' ? 'phone' : 'email'}`);
    setStep(2);
  };
  const handleVerify = () => {
    if (verificationCode.length < 4) {
      toast.error('Please enter a valid code');
      return;
    }
    // Simulate verification
    setTimeout(() => {
      setIsEnabled(true);
      setIsSettingUp(false);
      setStep(3);
      toast.success('Two-factor authentication enabled successfully');
    }, 1000);
  };
  const handleDisable = () => {
    if (window.confirm('Are you sure you want to disable 2FA? This will lower your account security.')) {
      setIsEnabled(false);
      toast.info('Two-factor authentication disabled');
    }
  };
  return <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-2090">
      <div className="flex items-start justify-between mb-6" data-id="element-2091">
        <div className="flex items-center" data-id="element-2092">
          <div className={`p-2 rounded-lg mr-4 ${isEnabled ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'}`} data-id="element-2093">
            <Shield className="h-6 w-6" data-id="element-2094" />
          </div>
          <div data-id="element-2095">
            <h3 className="text-lg font-bold text-gray-900" data-id="element-2096">
              Two-Factor Authentication
            </h3>
            <p className="text-sm text-gray-500" data-id="element-2097">
              Add an extra layer of security to your account.
            </p>
          </div>
        </div>

        {isEnabled ? <div className="flex items-center" data-id="element-2098">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 mr-4" data-id="element-2099">
              <CheckCircle className="w-3 h-3 mr-1" data-id="element-2100" /> Enabled
            </span>
            <button onClick={handleDisable} className="text-sm text-red-600 hover:text-red-700 font-medium" data-id="element-2101">
              Disable
            </button>
          </div> : <button onClick={handleStartSetup} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors" data-id="element-2102">
            Enable 2FA
          </button>}
      </div>

      {isSettingUp && <motion.div initial={{
      opacity: 0,
      height: 0
    }} animate={{
      opacity: 1,
      height: 'auto'
    }} className="border-t border-gray-100 pt-6" data-id="element-2103">
          {step === 1 && <div className="space-y-4" data-id="element-2104">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-2105">
                Select verification method
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2106">
                <button onClick={() => setMethod('sms')} className={`p-4 border rounded-xl flex items-center ${method === 'sms' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2107">
                  <Smartphone className={`h-5 w-5 mr-3 ${method === 'sms' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2108" />
                  <div className="text-left" data-id="element-2109">
                    <p className={`text-sm font-medium ${method === 'sms' ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-2110">
                      SMS Message
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2111">Code sent to mobile</p>
                  </div>
                </button>
                <button onClick={() => setMethod('email')} className={`p-4 border rounded-xl flex items-center ${method === 'email' ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-2112">
                  <Mail className={`h-5 w-5 mr-3 ${method === 'email' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2113" />
                  <div className="text-left" data-id="element-2114">
                    <p className={`text-sm font-medium ${method === 'email' ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-2115">
                      Email App
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-2116">Code sent to email</p>
                  </div>
                </button>
              </div>
              <div className="flex justify-end pt-2" data-id="element-2117">
                <button onClick={() => setIsSettingUp(false)} className="mr-3 px-4 py-2 text-sm text-gray-600 hover:text-gray-900" data-id="element-2118">
                  Cancel
                </button>
                <button onClick={handleSendCode} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700" data-id="element-2119">
                  Continue
                </button>
              </div>
            </div>}

          {step === 2 && <div className="space-y-4" data-id="element-2120">
              <h4 className="text-sm font-medium text-gray-900" data-id="element-2121">
                Enter verification code
              </h4>
              <p className="text-sm text-gray-500" data-id="element-2122">
                We sent a 6-digit code to your{' '}
                {method === 'sms' ? 'phone number' : 'email address'}.
              </p>
              <input type="text" value={verificationCode} onChange={e => setVerificationCode(e.target.value)} placeholder="000000" className="block w-full text-center text-2xl tracking-widest border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 py-3" maxLength={6} data-id="element-2123" />
              <div className="flex justify-end pt-2" data-id="element-2124">
                <button onClick={() => setStep(1)} className="mr-3 px-4 py-2 text-sm text-gray-600 hover:text-gray-900" data-id="element-2125">
                  Back
                </button>
                <button onClick={handleVerify} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700" data-id="element-2126">
                  Verify & Enable
                </button>
              </div>
            </div>}
        </motion.div>}
    </div>;
}